/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useCallback, useEffect, useState} from 'react';
// redux
import {useSelector, useDispatch} from 'react-redux';
import {fetchAccountSSOconfig, updateAccountSSOconfig} from "../../../../state/thunks/settings/companySettings";

// material
import {Switch, CopyField, CheckboxField, Separator, SingleSelectDropDown} from '@insticator/insticator-ui';
import SectionCard from '../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription
    from '../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription';
// styles
import injectSheet from 'react-jss';
import styles from './authentication_styles';
import { PRODUCT_COMMENTING, PRODUCT_COMMENTING_V2 } from "../../../../state/thunks/account";
import ConfigController from "../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import ExpandableSection from "../../../../material/expandablesection/expandablesection";


/* ========== ~~~~~~~~~~ ADVANCED SETTINGS - SENTIMENT ~~~~~~~~~~ ========== */
const AuthenticationConfig = injectSheet(styles)((props) => {
    const dispatch = useDispatch();
    // app state
    const {accountProducts, sites} = useSelector((state) => state.account);
    const {authentication} = useSelector((state) => state.companySettings);
    const {mocked} = useSelector(state => state.session);
    const hasCommenting = accountProducts?.includes(PRODUCT_COMMENTING) || accountProducts?.includes(PRODUCT_COMMENTING_V2);

    // local state
    const [isOpened, setIsOpened] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const { siteUUID: initialSiteUUID } = useSelector((state) => state.session);
    const [siteUUID, setSiteUUID] = useState(initialSiteUUID);
    const [publisherChecked, setPublisherChecked] = useState(authentication?.isCustomSsoEnabled || false);
    const [socialChecked, setSocialChecked] = useState(authentication?.isSocialSsoEnabled || false);
    const [config, setConfig] = useState(authentication);

    const [isScollIntoView, setIsScrollIntoView] = useState(false)

    useEffect(() => {
      let currentLocation = window.location.href;
      const hasUrlAnchor = currentLocation.includes("?view=authenticationConfig");
      if(hasUrlAnchor) setIsOpened(true)
    },[])
  
    useEffect(() => {
      if(isOpened && !isScollIntoView) scrollToConfig()
    }, [isOpened, isScollIntoView])
  
    const scrollToConfig= () => {
      let currentLocation = window.location.href;
      const hasUrlAnchor = currentLocation.includes("?view=authenticationConfig");
      if (hasUrlAnchor) {
        const anchorElem = document.getElementById('authentication-config');
        if(anchorElem){
            setTimeout(() => {
              anchorElem.scrollIntoView({ behavior: "smooth",  block: 'center'  })
              setIsScrollIntoView(true)
            }, 700)
        }
      }
    }

    const copyText = {
        publisherSSO: {
            title: 'Publisher SSO',
            description: 'Publisher is responsible for management of user account creation and will send us those credentials what we\'ll use to automatically Sign in users to commenting',
            allowCommentingOnly: 'Allow users to create a Commenting only account with COOL Media in addition to the publisher account they will have created via SSO.',
            tokenKey: 'SSO token key'
        },
        socialSSO: {
            title: 'Social SSO',
            description: 'Select social sites below that we\'ll show within our authentication process that users can use to Sign up or Sign in',
            facebook: 'Facebook SSO',
            google: 'Google SSO',
            twitter: 'Twitter SSO',

        },
        allowDisqus: {
            title: "Allow users to link Disqus account"
        }
    }

    useEffect(() => {
        if (siteUUID) dispatch(fetchAccountSSOconfig(siteUUID));
    }, [siteUUID]);

    useEffect(() => {
        if (authentication?.requestStatus === 'idle') {
            setConfig(authentication)
            setPublisherChecked(authentication?.isCustomSsoEnabled)
            setSocialChecked(authentication?.isSocialSsoEnabled)
        }
    }, [authentication])

    const toggleSocialChecked = () => {
        setIsChanged(!socialChecked !== authentication?.isSocialSsoEnabled || isChanged);

        setSocialChecked(!socialChecked)
        if (publisherChecked) {
            setPublisherChecked(false)
        }
    }
    const togglePublisherChecked = () => {
        setIsChanged(!publisherChecked !== authentication?.isCustomSsoEnabled || isChanged);
        setPublisherChecked(!publisherChecked)
        if (socialChecked) {
            setSocialChecked(false)
        }
    }

    const socialSelected = (socialId) => {
        return (config.ssoOptions.indexOf(socialId) >= 0)
    }

    const handleSocialCheck = (socialId) => {
        let socialArray = [...config.ssoOptions];

        if (!socialSelected(socialId)) {
            socialArray.push(socialId)
        } else if (config.ssoOptions.length){
                socialArray.splice(socialArray.indexOf(socialId), 1)
        }
        handleChange('ssoOptions', socialArray)
    }
    const handleAllowAccountCreationForOnlyCommentingCheck = () => {
        handleChange('allowAccountCreationForOnlyCommenting', !config.allowAccountCreationForOnlyCommenting)
    }

    const renderSectionGroupTitle = () => {
        return (
            // <div className={props.classes.SelectField}>
            //     <select
            //         onChange={e => setSiteUUID(e.target.value)}
            //         className={'select'}
            //         value={siteUUID || ''}
            //         type="text">
            //         {Object.keys(sites).map(key =>
            //             <option key={key} value={key}>{sites[key]}</option>)}
            //     </select>
            // </div>
            <SingleSelectDropDown 
              name={sites[siteUUID]} 
              searchable={false} 
              onSelect={selected => setSiteUUID(selected.value)} 
              items={Object.keys(sites).map(key => ({ name: sites[key], value: key, dir: 'ASC' }))}
            />
        )
    }

    const handleToggle = useCallback(() => {
        setIsOpened(!isOpened);
    }, [isOpened]);

    const handleCancel = () => {
        setPublisherChecked(authentication?.isCustomSsoEnabled )
        setSocialChecked(authentication?.isSocialSsoEnabled )
        setConfig({...authentication})
        setIsOpened(false);
        setIsChanged(false);
    }

    const handleSave = async () => {
        await dispatch(updateAccountSSOconfig({
            configUUID: config.uuid,
            requestData: {
                isSocialSsoEnabled: socialChecked,
                isCustomSsoEnabled: publisherChecked,
                allowAccountCreationForOnlyCommenting: config.allowAccountCreationForOnlyCommenting,
                ssoOptions:  socialChecked ? config.ssoOptions : []
            }}))
        setIsOpened(false)
        setIsChanged(false)
    };

    const handleChange =(settingName, settingValue) => {
        setConfig({
            ...config,
            [settingName]: settingValue,
        })
        setIsChanged(true)
    }
    const handleCopy = useCallback((fieldVal, e) => {
        navigator.clipboard.writeText(fieldVal)
    }, [config])


    const controller = (
        <ConfigController
            handleToggle={handleToggle}
            handleSave={handleSave}
            handleCancel={handleCancel}
            isOpened={isOpened}
            isChanged={isChanged}
            title='Authentication'
            description={''}
            cancelTitle={'Close'}
        />
    );
    return (
        <ExpandableSection
            className={`authenticationconfig ${props.classes.authenticationconfig}`}
            controller={controller}
            isOpened={isOpened}
            handleCancel={handleCancel}
            cancelTitle={'Close'}
        >
            {hasCommenting &&
                <div id="authentication-config">
                    <SectionCard klass={`${props.classes.authenticationadvancedsettings}`}>
                        {mocked &&
                        <SectionGroup>
                            {renderSectionGroupTitle()}
                            <SectionGroup>
                                <SectionGroup row>
                                    <Switch variant={'primary'} handleClick={() => togglePublisherChecked()}
                                            on={publisherChecked}/>
                                    <SectionGroupDescription
                                        title={copyText.publisherSSO.title}
                                        description={copyText.publisherSSO.description}
                                    />
                                </SectionGroup>
                                {/*disable when publisher sso not checked*/}
                                <SectionGroup row child disabled={!publisherChecked}>
                                    <CheckboxField type={`checkbox`}
                                                   name={`allow_comenting_only`}
                                                   valid={true}
                                                   checked={config.allowAccountCreationForOnlyCommenting}
                                                   handleChange={handleAllowAccountCreationForOnlyCommentingCheck}
                                                   label={copyText.publisherSSO.allowCommentingOnly}/>
                                </SectionGroup>
                                <SectionGroup child disabled={!publisherChecked}>
                                    <SectionGroupDescription
                                        title={copyText.publisherSSO.tokenKey}
                                    />
                                    <CopyField name={'copyfield'}
                                               defaultValue={authentication.customSsoSecretKey}
                                               handleCopy={(fieldVal, e) => handleCopy(fieldVal, e)} height={'inline'}
                                               readyOnly={true}
                                               ctaVariant={'primary'}
                                               ctaPosition={'inline'} fieldType={'text'}
                                               copyItemName={'Key'}
                                               disabled={!publisherChecked}
                                               margin={'16px'}/>
                                </SectionGroup>
                            </SectionGroup>

                            <Separator text={'or'} lineColor={'#EEF2F6'} textMargin={'15px'}/>

                            <SectionGroup>

                                <SectionGroup row>
                                    <Switch variant={'primary'} handleClick={() => toggleSocialChecked()} on={socialChecked}/>
                                    <SectionGroupDescription
                                        title={copyText.socialSSO.title}
                                        description={copyText.socialSSO.description}
                                    />
                                </SectionGroup>
                                {/*disable when social sso not checked */}
                                <SectionGroup row child disabled={!socialChecked}>
                                    <CheckboxField type={`checkbox`}
                                                   name={`facebook`}
                                                   valid={true}
                                                   checked={socialSelected('FACEBOOK')}
                                                   handleChange={() => handleSocialCheck('FACEBOOK')}
                                                   label={copyText.socialSSO.facebook}/>
                                </SectionGroup>
                                <SectionGroup row child disabled={!socialChecked}>
                                    <CheckboxField type={`checkbox`}
                                                   name={`google`}
                                                   valid={true}
                                                   checked={socialSelected('GOOGLE')}
                                                   handleChange={() => handleSocialCheck('GOOGLE')}
                                                   label={copyText.socialSSO.google}/>
                                </SectionGroup>
                                <SectionGroup row child disabled={!socialChecked}>
                                    <CheckboxField type={`checkbox`}
                                                   name={`twitter`}
                                                   valid={true}
                                                   checked={socialSelected('TWITTER')}
                                                   handleChange={() => handleSocialCheck('TWITTER')}
                                                   label={copyText.socialSSO.twitter}/>
                                </SectionGroup>
                            </SectionGroup>
                        </SectionGroup>}
                    </SectionCard>
                </div>
            }
        </ExpandableSection>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AuthenticationConfig;

